import { Grid } from '@material-ui/core';
import { icon_check } from 'components/base/images';
import AsyncFetchSelectWithAll from 'components/common/select/AsyncFetchSelectWithAll';
import CustomSpan from 'components/common/text/CustomSpan';
import { ChartItem, ProjectChartItem } from 'lib/types/dashboardTypes';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import useInput from 'lib/hooks/useInput';
import { projectStatusChartItemConverter } from 'lib/library';
import { useDidUpdateEffect } from 'lib/utils';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { DashboardActions, PartnerActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { color } from 'styles/utils';
import cx from 'classnames';
import _, { debounce } from 'lodash';
import { projectProcessFlagList } from 'lib/mapper';
import LineChart from 'components/common/chart/LineChart';
import { HasPagingData, OffsetPagingData } from 'lib/types/sharedTypes';
import useDateInput from 'lib/hooks/useDateInput';
import CustomDatePicker from 'components/common/input/CustomDatePicker';
import T from 'components/common/text/T';
import { useTranslation } from 'react-i18next';
import CheckIcon from 'components/base/icons/CheckIcon';
import CircularLoading from 'components/base/loading/CircularLoading';
import ListEmpty from 'components/common/list/ListEmpty';
import { PageSubTitle } from 'components/base/title/PageTitle';

interface ProjectStatusContainerProps {
  className?: string;
  isNormalLevelUser?: boolean;
}

interface IProjectGraph {
  groupDate: string;
  stage: number;
  totalCount: number;
}

interface IProjectPartners {
  accountType: number;
  isSelf: number;
  name: string;
  userGroupIdx: number;
  userGroupCode: string;
}

function ProjectStatusContainer({ className, isNormalLevelUser }: ProjectStatusContainerProps) {
  const {
    userGroupIdx,
    projectPartnersData: { list: projectPartnersList, pagingData: partnersPagingData },
    projectPartnersSuccess,
    projectPartnersPending,
    projectGraphData: { list: projectGraphList, pagingData: projectGraphPagingData },
    projectGraphSuccess,
    projectGraphPending,
    projectGraphFailure,
  }: {
    userGroupIdx: number;
    projectPartnersData: { list: IProjectPartners[]; pagingData: OffsetPagingData };
    projectPartnersSuccess: boolean;
    projectPartnersPending: boolean;
    projectGraphData: { list: IProjectGraph[]; pagingData: HasPagingData };
    projectGraphSuccess: boolean;
    projectGraphPending: boolean;
    projectGraphFailure: boolean;
  } = useShallowAppSelector(state => ({
    userGroupIdx: state.user.user?.userGroupIdx,
    projectPartnersData: state.partner.projectPartners.data || {},
    projectPartnersSuccess: state.partner.projectPartners.success,
    projectPartnersPending: state.partner.projectPartners.pending,
    projectGraphData: state.dashboard.projectGraph.data || {},
    projectGraphSuccess: state.dashboard.projectGraph.success,
    projectGraphPending: state.dashboard.projectGraph.pending,
    projectGraphFailure: state.dashboard.projectGraph.failure,
  }));

  const { t } = useTranslation();
  const [isCheckAll, setIsCheckAll] = useState<boolean>(true);
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const [projectPartners, setProjectPartners] = useState<IProjectPartners[]>([]);
  const setProjectPartnersValue = useMemo(
    () => projectPartnersSuccess && setProjectPartners(draft => draft.concat(projectPartnersList)),
    [!!projectPartnersSuccess],
  );
  const hasMoreProjectPartners = partnersPagingData?.page < partnersPagingData?.totalPage;
  const [projectChartItems, setProjectChartItems] = useState<ProjectChartItem[]>([]);
  const orderType = useInput('0');
  const projectPartnerKeyword = useInput('');
  const selectedProjectPartner = useInput('');
  // 0-new, 1-preparation, 2-scan, 3-cad, 4-cam, 5-milling, 6-postprocess, 7-complete
  const checkedProjectProcess = useCheckSetInput(new Set([1, 2, 3, 5, 6, 7]));
  // 1Y, 1M, 1W
  const period = useDateInput([moment().subtract(6, 'days'), moment()]);

  const [currentProjectDuration, setCurrentProjectDuration] = useState<moment.Moment | number>(
    moment().valueOf(),
  );

  const onFetchProjectPartners = debounce((first?: boolean) => {
    if (first) return PartnerActions.fetch_project_partners_request({ keyword: '', page: 1 });

    const { page, totalPage } = partnersPagingData;
    if (page >= totalPage) return;
    PartnerActions.fetch_project_partners_request({
      keyword: projectPartnerKeyword.value || '',
      page: page + 1,
    });
  }, 500);

  const onSearchProjectPartners = debounce((value: string) => {
    projectPartnerKeyword.setValue(value);
    setProjectPartners([]);
    PartnerActions.fetch_project_partners_request({ keyword: value || '', page: 1 });
  }, 500);

  const handleCheckAllStage = (checkAll: boolean) => {
    checkedProjectProcess.setValue(new Set([]));
    setIsCheckAll(!checkAll);
    if (!checkAll) {
      projectProcessFlagList.forEach((item: any) => {
        if (item.id === -3) return; // none
        if (item.id === -2) return; // urgent
        if (item.id === -1) return; // pending
        if (item.id === 0) return; // new
        if (item.id === 4) return; // cam
        checkedProjectProcess.onChange({ value: item.id });
      });
    }
  };

  const handleRefreshList = () => {
    DashboardActions.fetch_project_graph_request({
      startDate: period.value ? period.value[0].format('YYYY-MM-DD') : null,
      endDate: period.value ? period.value[1].format('YYYY-MM-DD') : null,
      stage: checkedProjectProcess.value.size ? [...checkedProjectProcess.value].join('%') : '',
      partnerGroupIdx: selectedProjectPartner.value || '',
    });
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  // SECTION: DidMount
  useEffect(() => {
    PartnerActions.fetch_project_partners_request({ keyword: '', page: 1 });
  }, []);

  useEffect(() => {
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  useDidUpdateEffect(() => {
    if (!projectGraphSuccess) return;
    setIsLoadSuccess(true);
  }, [projectGraphSuccess]);
  useDidUpdateEffect(() => {
    if (!projectGraphFailure) return;
    setIsLoadFailure(true);
  }, [projectGraphFailure]);

  // SECTION: DidUpdate
  useEffect(() => {
    if (period.value?.length === 2) {
      const diffDay = period.value[1].diff(period.value[0], 'days');

      // maxDate 365
      if (diffDay > 364) {
        const startDateValue = moment(period.value[0]);
        const endDateValue = moment(period.value[0]).add(364, 'days');
        return period.setValue([startDateValue, endDateValue]);
      }

      DashboardActions.fetch_project_graph_request({
        // isRequest: orderType.value,
        startDate: period.value ? period.value[0].format('YYYY-MM-DD') : null,
        endDate: period.value ? period.value[1].format('YYYY-MM-DD') : null,
        stage: checkedProjectProcess.value.size ? [...checkedProjectProcess.value].join('%') : '',
        partnerGroupIdx: selectedProjectPartner.value || '',
      });
    }
  }, [
    // currentProjectDuration,
    orderType.value,
    period.value,
    checkedProjectProcess.value,
    selectedProjectPartner.value,
  ]);

  // onChange projectGraphData
  useDidUpdateEffect(() => {
    // checkedProjectProcess.value 의 변경에 따라 projectGraphSuccess값 재 설정
    if (!projectGraphSuccess) return;

    let projectChartConfig: any = {
      new: {
        index: 0,
        color: color.stage_new,
        data: [],
      },
      preparation: {
        index: 1,
        color: color.stage_preparation,
        data: [],
      },
      scan: {
        index: 2,
        color: color.stage_scan,
        data: [],
      },
      cad: {
        index: 3,
        color: color.stage_cad,
        data: [],
      },
      cam: {
        index: 4,
        color: color.stage_cam,
        data: [],
      },
      milling: {
        index: 5,
        color: color.stage_milling,
        data: [],
      },
      post: {
        index: 6,
        color: color.stage_post,
        data: [],
      },
      complete: {
        index: 7,
        color: color.stage_completed,
        data: [],
      },
    };

    Object.keys(projectChartConfig).forEach((key, idx) => {
      const currentItems = projectGraphList?.filter((o: any) => o.stage === idx);

      if (idx === projectChartConfig[key].index) {
        if (period.value?.length !== 2) return;

        const projectGraphValue: ChartItem[] | undefined = projectStatusChartItemConverter({
          items: currentItems,
          startDate: period.value[0],
          endDate: period.value[1],
          yKey: 'totalCount',
        });

        projectChartConfig[key].data = projectGraphValue || [];
      }
    });

    const projectChartItemsValue = [...checkedProjectProcess.value].reduce((acc, curr) => {
      const currentChartStage =
        Object.keys(projectChartConfig).find(key => {
          return projectChartConfig[key].index === curr;
        }) || 'new';

      const obj = {
        index: projectChartConfig[currentChartStage].index,
        id: currentChartStage,
        color: projectChartConfig[currentChartStage].color,
        data: projectChartConfig[currentChartStage].data,
      };
      return _.orderBy(acc.concat(obj), 'index', 'desc');
    }, []);

    setProjectChartItems(projectChartItemsValue);
  }, [projectGraphSuccess]);

  useDidUpdateEffect(() => {
    if (checkedProjectProcess.value.size === 6) {
      setIsCheckAll(true);
    } else {
      setIsCheckAll(false);
    }
  }, [checkedProjectProcess.value, isCheckAll, projectProcessFlagList]);

  return (
    <StyledProjectStatus data-component-name="ProjectStatus" className={className}>
      <div className="dashboard__card_title_box">
        <h2 className="dashboard__card_title">
          <PageSubTitle title="HOME_TITLE_PROJECT_STATUS" margin="0" />
        </h2>
      </div>

      <Grid
        item
        className={cx('dashboard__project_chart box-shadow-default', {
          employee: isNormalLevelUser,
        })}
      >
        <Grid item className="dashboard__project_status border-radius-default">
          <div className="dashboard__project_status_option_box">
            <div className="dashboard__project_status_option_duration">
              <CustomSpan fontSize={15} fontWeight={700}>
                <T>GLOBAL_PERIOD</T>
              </CustomSpan>
              <CustomDatePicker
                type="rangeDate"
                testId="project_status_date_picker"
                fullWidth
                height={32}
                borderColor="#868686"
                fontColor="#666"
                fontSize={12}
                value={period.value}
                onChange={period.onChange}
                className={cx({
                  error: period.value !== null ? !period.value : false,
                })}
              />
            </div>
            <div className="dashboard__project_status_option_partner">
              <CustomSpan fontSize={15} fontWeight={700}>
                <T>GLOBAL_PARTNER</T>
              </CustomSpan>
              <AsyncFetchSelectWithAll
                testId="project_status_partner"
                className="sm"
                fullWidth
                fontColor="#666"
                borderColor="#868686"
                hoverBorderColor="#868686"
                activeBorderColor="#868686"
                bgColor="transparent"
                loadingIconColor="#868686"
                dropIconColor="#868686"
                inputProps={{
                  hasInitialValue: true,
                  selectedValue: selectedProjectPartner.value,
                  setSelectedValue: selectedProjectPartner.setValue,
                  data: projectPartners,
                  hasMoreData: hasMoreProjectPartners,
                  initialValueName: t('GLOBAL_ALL'),
                  idKey: 'userGroupIdx',
                  labelKey: 'name',
                  onFetch: onFetchProjectPartners,
                  searchLoading: projectPartnersPending,
                  onSearch: onSearchProjectPartners,
                }}
              />
            </div>
          </div>
        </Grid>
        <Grid item className="dashboard__project_status filterBox_wrapper">
          <div className="dashboard__project_status_filter_process">
            <div className="dashboard__project_status_filter_box">
              <ul className="dashboard__project_status_filter_list">
                <li
                  className={cx(`dashboard__project_status_filter_item`, {
                    active: isCheckAll,
                  })}
                  onClick={() => {
                    handleCheckAllStage(isCheckAll);
                  }}
                >
                  <span
                    className={`dashboard__project_status_filter_check`}
                    style={{
                      borderColor: isCheckAll ? 'transparent' : '#ccc',
                      backgroundColor: isCheckAll ? '#000' : 'transparent',
                    }}
                  >
                    {isCheckAll && <CheckIcon />}
                  </span>
                  All
                </li>
                {projectProcessFlagList.map((item, idx) => {
                  if (item.id === -1) return; //pending
                  if (item.id === 0) return; // new
                  if (item.id === 4) return; // CAM
                  if (item.id === -2) return; // Urgent
                  if (item.id === -3) return; // None
                  return (
                    <li
                      className={cx(`dashboard__project_status_filter_item`, {
                        active: checkedProjectProcess.value.has(item.id),
                      })}
                      key={idx}
                      onClick={() => checkedProjectProcess.onChange({ value: item.id })}
                    >
                      <span
                        className={`dashboard__project_status_filter_check ${item.index}`}
                        style={{
                          backgroundColor: checkedProjectProcess.value.has(item.id)
                            ? item.color
                            : 'transparent',
                        }}
                      >
                        <img src={icon_check} alt="check icon" />
                      </span>{' '}
                      {item.name}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Grid>

        <div className="dashboard__project_chart_item">
          {!isLoadSuccess && !!projectGraphPending && (
            <div className="infinite-scroll-loading progress">
              <CircularLoading />
            </div>
          )}
          {useMemo(
            () =>
              !!isLoadSuccess && (
                <LineChart
                  className="overview__chart"
                  width={750}
                  height={280}
                  isMultipleData
                  isRenderTick
                  data={projectChartItems}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 50,
                    left: 50,
                  }}
                  axisBottom={{
                    tickRotation: 0,
                  }}
                />
              ),
            [projectChartItems, isLoadSuccess],
          )}
          {!!isLoadFailure && (
            <ListEmpty
              text="GLOBAL_LIST_ERROR"
              isRefreshButton={true}
              onRefresh={handleRefreshList}
            />
          )}
        </div>
      </Grid>
    </StyledProjectStatus>
  );
}

const StyledProjectStatus = styled.section<{}>`
  .dashboard__project_chart {
    padding: 30px;
    background-color: #fff;
    border-radius: 10px;
    width: 810px;
    height: 400px;
    .dashboard__project_status {
      display: flex;
      justify-content: space-between;
      h2 {
        font-size: 26px;
        font-weight: 700;
      }

      .dashboard__project_status_order_btn_box {
        position: relative;
        height: 36px;
        .dashboard__project_status_order_btn {
          width: 100%;
          height: 100%;
          font-weight: 500;
          &.active {
            background-color: white;
            color: ${color.black_font};
          }
        }
      }
      [class*='dashboard__project_status_option'] {
        .asyncFetchSelect,
        .datePicker__box,
        .muiWrapper {
        }
        .MuiInputBase-root {
          background-color: transparent;
        }
        .MuiOutlinedInput-notchedOutline {
        }
        .MuiSelect-icon {
        }
      }
      .dashboard__project_status_filter_process {
        display: flex;
        align-items: center;
        padding-top: 15px;
        width: 100%;
        h3 {
          display: flex;
          align-items: center;
          font-size: 13px;
          svg {
            margin-right: 10px;
          }
        }
        .dashboard__project_status_filter_box {
          width: 100%;
          .dashboard__project_status_filter_list {
            width: 100%;
            display: flex;
            justify-content: space-between;
            .dashboard__project_status_filter_item {
              display: flex;
              align-items: center;
              height: 35px;
              padding: 0 7px;
              border-radius: 5px;
              font-size: 14px;
              cursor: pointer;

              .dashboard__project_status_filter_check {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                margin-right: 10px;
                width: 16px;
                height: 16px;
                border-radius: 4px;
                background-color: transparent;
                border: 1px solid #868686;
              }
              &.active {
                color: ${color.black_font};
                .dashboard__project_status_filter_check {
                  border: 0px solid #868686;
                }
              }
            }
          }
        }
      }
      .dashboard__project_status_option_box {
        display: flex;
        white-space: nowrap;
        .dashboard__project_status_option_duration,
        .dashboard__project_status_option_partner {
          align-items: center;
          display: flex;
          column-gap: 10px;
          .datePicker__box {
            width: 210px;
            .ant-picker-input {
              width: 65px;
            }
            .ant-picker {
            }
          }
        }
        .dashboard__project_status_option_duration {
          margin-right: 16px;
        }
      }
    }

    &.employee {
      width: 690px;
    }
    .dashboard__project_chart_item {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 750px;
      height: 280px;
      .progress {
        z-index: 9999;
      }
    }
    .dashboard__project_chart_regends {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      margin: 20px auto 15px;
      min-height: 24px;
      .dashboard__project_chart_duration_box {
        line-height: 0;
      }
      .dashboard__project_chart_prev_icon_btn {
        transform: rotate(-180deg);
      }
      .dashboard__project_chart_regends_period {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 285px;
        height: 100%;
        font-size: 13px;
        background-color: #b5b7c1;
        border-radius: 5px;
        span {
          margin: 0 5px;
        }
      }
      .dashboard__project_chart_regends_category_list {
        display: flex;
        align-items: center;
        font-size: 12px;
        .dashboard__project_chart_regends_category_item {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          &:not(:first-child) {
            margin-left: 30px;
          }
          .dashboard__project_chart_regends_category_symbol {
            display: inline-block;
            margin-right: 10px;
            width: 12px;
            height: 12px;
            border-radius: 2px;
          }
        }
      }
    }
  }
`;

export default React.memo(ProjectStatusContainer);
